import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import {
  Eye, EyeOff, ShoppingBag, Package, ShoppingCart, Users,
  BarChart2, ArrowRight, Shield, Mail, Lock,
} from "lucide-react";
import { AUTH } from "@/constants/testIds";

const BG_IMAGE = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const FEATURES = [
  { icon: Package,      label: "Products",  sub: "Management", bg: "bg-violet-100",   color: "text-violet-600" },
  { icon: ShoppingCart, label: "Order",     sub: "Management", bg: "bg-amber-100",    color: "text-amber-600"  },
  { icon: Users,        label: "Reseller",  sub: "Management", bg: "bg-emerald-100",  color: "text-emerald-600"},
  { icon: BarChart2,    label: "Analytics", sub: "& Reports",  bg: "bg-red-100",      color: "text-red-500"    },
];

export default function Login() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [keepSigned, setKeepSigned] = useState(true);
  const [loading, setLoading]   = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin!");
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left Panel ──────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Background image */}
        <img src={BG_IMAGE} alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        {/* Warm overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/90 via-amber-50/80 to-slate-100/70" />
        {/* Purple decorative blob */}
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-orange-400/20 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <BrandLogo size="lg" />
          </div>
          <p className="text-xs text-slate-500 -mt-6 ml-12">Import Smart. Sell Better.</p>

          {/* Hero text */}
          <div className="mt-auto mb-8">
            <h1 className="text-5xl font-black text-slate-900 leading-tight mb-3">
              Manage Your<br />Business{" "}
              <span className="text-orange-500">Smarter</span>
            </h1>
            <p className="text-slate-600 text-lg max-w-md leading-relaxed">
              One platform to manage products, inventory,<br />orders, resellers and more.
            </p>

            {/* Feature tiles */}
            <div className="grid grid-cols-4 gap-3 mt-8 max-w-md">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div className={`w-14 h-14 ${f.bg} rounded-2xl flex items-center justify-center shadow-sm`}>
                    <f.icon className={`w-6 h-6 ${f.color}`} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 text-center leading-tight">{f.label}<br />{f.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ─────────────────────────────────────────────── */}
      <div className="w-full lg:w-[480px] flex items-center justify-center bg-white p-6 lg:p-10 relative">
        {/* Decorative purple circle top-right */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-bl-full" />
        <div className="absolute bottom-0 right-0 w-20 h-20 bg-orange-400/10 rounded-tl-full" />

        <div className="w-full max-w-[380px] relative z-10">
          {/* Logo (mobile only — hidden on desktop where left panel shows it) */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <BrandLogo size="md" />
          </div>

          {/* Desktop logo inside form panel */}
          <div className="hidden lg:flex items-center gap-2 mb-7">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center shadow">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <BrandLogo size="md" />
              <p className="text-[11px] text-slate-400 -mt-0.5">Import Smart. Sell Better.</p>
            </div>
          </div>

          {/* Heading */}
          <h2 className="text-3xl font-black text-slate-900 mb-1">Admin Login</h2>
          <p className="text-sm text-slate-500 mb-7">Sign in to access your admin panel</p>

          {/* Form */}
          <form data-testid={AUTH.loginForm} onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid={AUTH.emailInput}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@importrend.com"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid={AUTH.passwordInput}
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Keep signed in + Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={keepSigned} onChange={e => setKeepSigned(e.target.checked)}
                  className="w-4 h-4 accent-indigo-600 rounded" />
                <span className="text-sm text-slate-600">Keep me signed in</span>
              </label>
              <button type="button" className="text-sm font-semibold text-indigo-600 hover:underline">
                Forgot Password?
              </button>
            </div>

            {/* Submit */}
            <button
              data-testid={AUTH.loginButton}
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold py-3.5 rounded-xl transition-all shadow hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Signing in...</>
              ) : (
                <>Sign In <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          {/* Social divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">Or continue with</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Social buttons (UI only) */}
          <div className="grid grid-cols-2 gap-3">
            <button type="button"
              className="flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700">
              {/* Google G */}
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
            <button type="button"
              className="flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700">
              {/* Microsoft */}
              <svg className="w-4 h-4" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Microsoft
            </button>
          </div>

          {/* Security badge */}
          <div className="flex items-start gap-3 mt-6 bg-indigo-50 rounded-xl p-3.5">
            <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-indigo-800">Secure Admin Access</p>
              <p className="text-[11px] text-indigo-600 mt-0.5">Your data is protected with industry standard security</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
